// ─── Configuración ───────────────────────────────────────────────
// ID del Meta Pixel. Si está vacío no se carga el pixel y cada evento
// se imprime con console.info para poder probar.
export const META_PIXEL_ID = "";
// ─────────────────────────────────────────────────────────────────

import { uuid } from "./leadCapture";

export type CtaSource =
  | "hero"
  | "sticky"
  | "calculator"
  | "final"
  | "topbar"
  | "video"
  | "how"
  | "included"
  | "founding"
  | "leak";

/** Only these keys ever leave the browser. Never PII or free text. */
export type TrackParams = Partial<{
  step: number;
  industry: string;
  revenue_bucket: string;
  role: string;
  problem: string;
  source: CtaSource;
}>;

const ALLOWED_KEYS: (keyof TrackParams)[] = [
  "step",
  "industry",
  "revenue_bucket",
  "role",
  "problem",
  "source",
];

type FbqFn = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  push: FbqFn;
  loaded: boolean;
  version: string;
};

declare global {
  interface Window {
    fbq?: FbqFn;
    _fbq?: FbqFn;
  }
}

let pixelInitialized = false;
const firedOnce = new Set<string>();

export function initPixel(): void {
  if (pixelInitialized) return;
  pixelInitialized = true;

  if (!META_PIXEL_ID) {
    console.info("[tracking] Meta Pixel desactivado (META_PIXEL_ID vacío).");
    track("PageView");
    return;
  }

  if (!window.fbq) {
    const fbq = function (...args: unknown[]) {
      if (fbq.callMethod) fbq.callMethod(...args);
      else fbq.queue.push(args);
    } as FbqFn;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];
    window.fbq = fbq;
    window._fbq = fbq;

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);
  }

  window.fbq?.("init", META_PIXEL_ID);
  track("PageView");
}

/**
 * Sends a Meta event. Standard events use fbq('track'), custom events use
 * fbq('trackCustom'). Always passes an eventID for Conversions API dedup.
 */
export function track(
  eventName: string,
  params: TrackParams = {},
  options: { custom?: boolean } = {}
): string {
  const eventID = uuid();
  const clean = sanitize(params);
  const method = options.custom ? "trackCustom" : "track";

  if (!META_PIXEL_ID || !window.fbq) {
    console.info(`[tracking] ${method} ${eventName}`, clean, { eventID });
    return eventID;
  }
  window.fbq(method, eventName, clean, { eventID });
  return eventID;
}

/** Same as track(), but fires at most once per page load. */
export function trackOnce(
  eventName: string,
  params: TrackParams = {},
  options: { custom?: boolean } = {}
): void {
  if (firedOnce.has(eventName)) return;
  firedOnce.add(eventName);
  track(eventName, params, options);
}

function sanitize(params: TrackParams): TrackParams {
  const out: Record<string, string | number> = {};
  ALLOWED_KEYS.forEach((key) => {
    const value = params[key];
    if (typeof value === "number" || (typeof value === "string" && value !== "")) {
      out[key] = value;
    }
  });
  return out as TrackParams;
}
